// PRPathways.js - Australia
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, MapPin, Users, ChevronDown, ChevronUp } from 'lucide-react';

const PRPathways = () => {
  const [expandedPathway, setExpandedPathway] = useState(null);

  const pathways = [
    {
      icon: Zap,
      title: 'General Skilled Migration (GSM)',
      subtitle: 'Subclass 189 Skilled Independent, Subclass 190 Skilled Nominated, Subclass 491 Skilled Work Regional',
      description: 'The main route for skilled workers with nobody sponsoring them. Applicants are ranked on points, which makes it the busiest pathway of the three.',
      details: {
        overview: 'Under GSM your profile is scored against a points test, then ranked in SkillSelect alongside everyone else who has registered interest in the same occupation.',
        programs: [
          {
            name: 'Skilled Independent Visa (Subclass 189)',
            requirements: 'A permanent visa that needs no sponsor at all, whether an employer, a state or a family member. Your occupation must sit on the Medium and Long-term Strategic Skills List (MLTSSL), you need at least 65 points, and English has to reach competent level, equivalent to IELTS 6.0.'
          },
          {
            name: 'Skilled Nominated Visa (Subclass 190)',
            requirements: 'Permanent residency backed by a state or territory. Your occupation has to appear on that jurisdiction\'s list, the nomination itself is worth 5 points, the total including it still has to reach 65 points, and you agree to live in the nominating state for 2 years.'
          },
          {
            name: 'Skilled Work Regional (Provisional) Visa (Subclass 491)',
            requirements: 'A provisional visa for regional Australia, valid 5 years. You need either state or territory nomination or sponsorship from a relative in a regional area, worth 15 points towards the 65 point minimum. Three years of living and working regionally then opens the door to permanent residency through Subclass 191.'
          }
        ],
        keyPoints: [
          'Your score is built from age, English, work history, education and a few further factors',
          'The scale reaches 120+ points, though invitations generally go to totals of 80-90 or above',
          'Your nominated occupation has to sit on the skilled occupation list that applies to the visa',
          'A positive skills assessment from the relevant assessing authority is mandatory',
          'Interest is registered by lodging an Expression of Interest (EOI) in SkillSelect',
          'Invitation rounds run on a regular cycle, usually monthly'
        ]
      }
    },
    {
      icon: MapPin,
      title: 'State and Territory Nomination',
      subtitle: 'New South Wales, Victoria, Queensland, South Australia, Western Australia, Tasmania, ACT, NT',
      description: 'Each jurisdiction can nominate skilled migrants whose occupations answer shortages in its own labour market.',
      details: {
        overview: 'Nomination is run separately by every Australian state and territory, so occupation lists, points expectations and extra conditions all differ from one to the next.',
        programs: [
          {
            name: 'New South Wales (NSW)',
            requirements: 'Covers Sydney as well as regional NSW, with occupation lists revised on a regular basis. Several streams look for a job offer or an existing tie to the state, and competitive totals usually start around 80-90 points.'
          },
          {
            name: 'Victoria',
            requirements: 'Aimed at occupations the state is short of, with separate streams for graduates and for experienced workers. Most streams expect either a Victorian qualification or a job offer. Melbourne and regional Victoria each have their own routes.'
          },
          {
            name: 'Queensland',
            requirements: 'Programmes run for Brisbane and for regional Queensland, and the occupation lists differ between them. Already working or living in the state counts in your favour, and regional migration is a clear priority.'
          },
          {
            name: 'South Australia',
            requirements: 'Thresholds here are comparatively low, in the 65-70 point range, and regional settlement is strongly favoured. Chain migration provisions exist for family members, and the occupation lists are refreshed often.'
          },
          {
            name: 'Western Australia',
            requirements: 'Both graduate and skilled worker streams operate, focused on occupations WA is short of. A job offer or a qualification earned in the state helps considerably. Perth and regional WA both have pathways open.'
          },
          {
            name: 'Tasmania',
            requirements: 'Generally the most reachable of the state programmes, with lower points expectations than elsewhere. A job offer or a Tasmanian qualification opens the main routes, and the state works actively to attract skilled migrants.'
          }
        ],
        keyPoints: [
          'A 190 nomination adds 5 points; a 491 nomination adds 15',
          'Occupation lists and selection criteria are set independently by each jurisdiction',
          'Many streams ask for a job offer or an existing connection to the state',
          'Regional programmes are often easier to qualify for than metropolitan ones',
          'You commit to living in whichever state nominates you',
          'Nomination decisions take between 2 and 12 weeks depending on the state'
        ]
      }
    },
    {
      icon: Users,
      title: 'Employer Sponsored & Family',
      subtitle: 'Subclass 186 employer nomination, Subclass 820/801 partner, Subclass 103/143 parent',
      description: 'Routes that rest on an Australian employer or a family relationship rather than on a points score.',
      details: {
        overview: 'Where a points-tested visa is out of reach, sponsorship by an employer or by a close relative already in Australia can still lead to permanent residency.',
        programs: [
          {
            name: 'Employer Nomination Scheme (Subclass 186)',
            requirements: 'Permanent residency for skilled workers nominated by an Australian employer. You need 3 years of experience in the nominated occupation, competent English, a skills assessment and the employer\'s nomination. Applications run through either the Direct Entry stream or the Temporary Residence Transition stream.'
          },
          {
            name: 'Partner Visa (Subclass 820/801)',
            requirements: 'For the partner of an Australian citizen or permanent resident. The temporary 820 comes first, the permanent 801 follows. You have to show a genuine relationship and that you live together, or give compelling reasons why you do not, and your sponsor must be settled in Australia. Processing runs 24-36 months.'
          },
          {
            name: 'Parent Visa (Subclass 103/143)',
            requirements: 'For parents of an Australian citizen or permanent resident, subject to the balance of family test, which asks that at least half your children live in Australia. The contributory stream (143) costs far more but moves faster; the non-contributory 103 queue stretches beyond 30 years.'
          }
        ],
        keyPoints: [
          'Employer routes need an approved sponsor and a genuine, ongoing position',
          'Partner applications stand or fall on the depth of your relationship evidence',
          'Parent visas carry long queues and high costs',
          'None of these three pathways involves the points test',
          'A skills assessment may still be required under employer sponsorship',
          'Health and character checks apply whichever route you take'
        ]
      }
    }
  ];

  const togglePathway = (index) => {
    setExpandedPathway(expandedPathway === index ? null : index);
  };

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            Routes into Australian Permanent Residency
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Open any route below to see what it asks of you, then decide which one fits your background and plans.
          </p>
        </motion.div>

        {/* Pathways */}
        <div className="space-y-6">
          {pathways.map((pathway, index) => {
            const Icon = pathway.icon;
            const isExpanded = expandedPathway === index;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                {/* Pathway Header */}
                <button
                  onClick={() => togglePathway(index)}
                  className="w-full p-6 flex items-start gap-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={24} className="text-white" />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-primary mb-2">
                      {pathway.title}
                    </h3>
                    <p className="text-sm text-gray-500 font-light mb-2">
                      {pathway.subtitle}
                    </p>
                    <p className="text-gray-600 text-sm font-light">
                      {pathway.description}
                    </p>
                  </div>

                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronUp size={24} className="text-secondary" />
                    ) : (
                      <ChevronDown size={24} className="text-secondary" />
                    )}
                  </div>
                </button>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 border-t border-gray-200">
                        <div className="pt-6 space-y-6">
                          {/* Overview */}
                          <div>
                            <h4 className="text-base font-semibold text-primary mb-2">
                              Overview
                            </h4>
                            <p className="text-gray-600 text-sm font-light leading-relaxed">
                              {pathway.details.overview}
                            </p>
                          </div>

                          {/* Programs/Categories */}
                          <div>
                            <h4 className="text-base font-semibold text-primary mb-3">
                              Programs & Requirements
                            </h4>
                            <div className="space-y-4">
                              {pathway.details.programs.map((program, pIndex) => (
                                <div key={pIndex} className="bg-surface p-4 rounded-lg">
                                  <h5 className="text-sm font-semibold text-primary mb-2">
                                    {program.name}
                                  </h5>
                                  <p className="text-gray-600 text-sm font-light leading-relaxed">
                                    {program.requirements}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Key Points */}
                          <div>
                            <h4 className="text-base font-semibold text-primary mb-3">
                              Key Points
                            </h4>
                            <ul className="space-y-2">
                              {pathway.details.keyPoints.map((point, kIndex) => (
                                <li key={kIndex} className="flex items-start gap-2">
                                  <span className="w-1.5 h-1.5 bg-secondary rounded-full mt-2 flex-shrink-0"></span>
                                  <span className="text-gray-600 text-sm font-light">
                                    {point}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PRPathways;
